/**
 * Storico modifiche di un report ENISA dal log audit (E7.6), letto da product-service
 * (GET /api/products/audit/events, gated VIEW_AUDIT_LOG).
 *
 * L'srp-service audita ogni mutazione del report come `srp_report` con entityId = id del report,
 * e la correzione dell'ancora del clock CRA come `sla_timer` con entityId = id del timer: lo
 * storico unisce le due serie.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { createServiceClient } from '../../api/client.js';

export interface SrpAuditEvent {
  eventId: string;
  timestamp: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string;
  service: string;
  before?: unknown;
  after?: unknown;
}

interface AuditQueryResult {
  items: SrpAuditEvent[];
  total: number;
}

const productApi = createServiceClient('product');

/** Eventi per pagina (massimo consentito da GET /audit/events). */
const HISTORY_PAGE_SIZE = 200;

/** Tetto di pagine lette per entità (difesa da loop): 50 × 200 = 10.000 eventi. */
const MAX_HISTORY_PAGES = 50;

/**
 * Tutti gli eventi di un'entità, pagina per pagina (200 per pagina, il massimo dell'API): il
 * modulo ENISA scrive una voce per campo, quindi lo storico di un report può superare una pagina.
 */
async function fetchEntityEvents(entityType: string, entityId: string): Promise<SrpAuditEvent[]> {
  const eventi: SrpAuditEvent[] = [];
  for (let page = 1; page <= MAX_HISTORY_PAGES; page++) {
    const qs = new URLSearchParams({
      entityType,
      entityId,
      pageSize: String(HISTORY_PAGE_SIZE),
      page: String(page),
    });
    const res = await productApi<AuditQueryResult>(`/audit/events?${qs.toString()}`);
    eventi.push(...res.items);
    if (res.items.length < HISTORY_PAGE_SIZE || eventi.length >= res.total) break;
  }
  return eventi;
}

/**
 * Eventi audit del report (e del suo timer SLA, se noto), dal più recente.
 * Abilitato solo se `enabled` (pannello aperto + permesso VIEW_AUDIT_LOG).
 */
export async function fetchReportAuditHistory(
  reportId: number,
  timerId?: number,
): Promise<SrpAuditEvent[]> {
  const [report, timer] = await Promise.all([
    fetchEntityEvents('srp_report', String(reportId)),
    timerId !== undefined ? fetchEntityEvents('sla_timer', String(timerId)) : Promise.resolve([]),
  ]);
  return [...report, ...timer].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}

/** Sotto-chiave di `srpKeys.auditHistory(reportId)`: le mutazioni invalidano per prefisso. */
const historyKey = (reportId: number, timerId?: number) =>
  ['srp', 'audit-history', reportId, timerId ?? null] as const;

export function useReportAuditHistory(
  reportId: number,
  timerId: number | undefined,
  enabled: boolean,
): UseQueryResult<SrpAuditEvent[]> {
  return useQuery({
    queryKey: historyKey(reportId, timerId),
    queryFn: () => fetchReportAuditHistory(reportId, timerId),
    enabled: enabled && reportId > 0,
  });
}
